import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { fetchAuthSession } from "aws-amplify/auth"
import {
  GatedAutoPaySelectCardFailReason,
  PromoFailReason,
} from "ipctg-inflow-types/InFlowEnums"
import {
  GatedAutoPaySelectCardPOSTSuccessResponse,
  GatedPromoRegisterPOSTRequest,
  GatedPromoRegisterPOSTSuccessResponse,
  GatedRegisterSessionPOSTSuccessResponse,
} from "ipctg-inflow-types/InFlowGatedAPI"
import { useState } from "react"
import { GridList, GridListItem } from "react-aria-components"
import { Controller, useForm } from "react-hook-form"
import { Trans, useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { Button } from "@/globalComponents/atoms/Button"
import Card from "@/globalComponents/atoms/Card"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Icon from "@/globalComponents/atoms/Icon"
import Text from "@/globalComponents/atoms/Text"
import TextField from "@/globalComponents/molecules/TextField"
import { apiCardBrandToIconName } from "@/utilities/etc"
import {
  GatedAutoPaySelectCardError,
  GatedLookupSessionsError,
  GatedPromoRegisterError,
  GatedRegisterSessionError,
} from "@/utilities/expectedErrors"
import { requiredRule } from "@/utilities/form"
import { RATES_HREF } from "@/utilities/globals"
import { sessionDetailsQueryOptions } from "@/utilities/hooks/queries/express"
import { setupIntentQueryOptions } from "@/utilities/hooks/queries/payment"
import {
  meQueryOptions,
  savedPaymentMethodsOptions,
} from "@/utilities/hooks/queries/user"
import { useErrorModal } from "@/utilities/hooks/useErrorModal"
import { useTheme } from "@/utilities/hooks/useTheme"
import useToggle from "@/utilities/hooks/useToggle"

import PayingCashLauncher from "../../-components/PayingCash/PayingCashLauncher"
import { AeronauticStripe } from "../../../welcome"

import { DiscountCodeSchema } from "./cart"
import styles from "./express.module.css"

export const Route = createFileRoute(
  "/qr/$qrLookupID/gated/register/$sessionID/express"
)({
  component: RouteComponent,
  beforeLoad: async ({ context, params, location }) => {
    if (!context.isAuthenticated) {
      throw redirect({
        to: "/auth/sign-in",
        search: {
          redirect: location.href,
          qrLookupID: params.qrLookupID,
        },
      })
    }
    context.queryClient.fetchQuery(meQueryOptions())
    context.queryClient.fetchQuery(savedPaymentMethodsOptions())
  },
})

const CUSTOM_PAYMENT_METHOD = "custom"

function RouteComponent() {
  const { t } = useTranslation()
  const { sessionID, qrLookupID } = Route.useParams()
  const navigate = Route.useNavigate()
  const { setError } = useErrorModal()
  const locationName =
    useTheme().scannedArea?.theme.locationInformation.locationName
  const { data: sessionDetails } = useSuspenseQuery(
    sessionDetailsQueryOptions({ sessionID })
  )
  const { data: savedPaymentMethodsRsp } = useSuspenseQuery(
    savedPaymentMethodsOptions()
  )
  const { data: me } = useSuspenseQuery(meQueryOptions())
  const hasSavedPaymentMethods = !!savedPaymentMethodsRsp.paymentMethods.length

  const [paymentMethod, setPaymentMethod] = useState<Set<string>>(() => {
    const paymentMethodID = !hasSavedPaymentMethods
      ? CUSTOM_PAYMENT_METHOD
      : savedPaymentMethodsRsp.paymentMethods.find((pm) => pm.isDefault)
          ?.paymentMethodID ||
        savedPaymentMethodsRsp.paymentMethods[0].paymentMethodID
    return new Set([paymentMethodID])
  })

  const { mutate, isPending } = useMutation({
    mutationFn,
    onSuccess,
    onError,
  })

  return (
    <>
      <Heading
        className="center"
        level={4}
        as={1}
        style={{ color: "var(--secondary-11)" }}
      >
        <Trans i18nKey="welcome.header" values={{ locationName }}>
          Welcome to <span>{locationName}</span> Parking
        </Trans>
      </Heading>
      <Card>
        <div>
          <Heading level={1} as={2} className="center">
            {t("welcome.subhead")}
          </Heading>
          <AeronauticStripe />
        </div>
        <Text className="center">
          <Trans
            i18nKey={
              hasSavedPaymentMethods
                ? "register.express.description"
                : "register.express.descriptionNoSavedPaymentMethod"
            }
            components={{
              1: (
                <a
                  className="body--1 semi-bold"
                  href={RATES_HREF}
                  target="_blank"
                />
              ),
            }}
          />
        </Text>
        <VehicleCell plate={sessionDetails.session.cameraPlate} />
        {hasSavedPaymentMethods && (
          <Heading level={3} className="center" id="payment-section-header">
            {t("register.express.paymentSectionHeader")}
          </Heading>
        )}
        <PromoCodeSection
          plate={sessionDetails.session.cameraPlate}
          hasSavedPaymentMethods={hasSavedPaymentMethods}
        />
        {hasSavedPaymentMethods && (
          <GridList
            aria-labelledby="payment-section-header"
            items={[
              ...savedPaymentMethodsRsp.paymentMethods.sort(
                (a, b) => Number(b.isDefault) - Number(a.isDefault)
              ),
              { type: "custom" },
            ]}
            selectionMode="single"
            selectionBehavior="toggle"
            dependencies={savedPaymentMethodsRsp.paymentMethods}
            selectedKeys={paymentMethod}
            onSelectionChange={(keys) => {
              // Shouldn't happen
              if (typeof keys === "string") {
                setPaymentMethod(
                  new Set([
                    savedPaymentMethodsRsp.paymentMethods[0].paymentMethodID,
                  ])
                )
              } else {
                const paymentMethodID = keys.values().next().value?.toString()
                setPaymentMethod(
                  paymentMethodID ? new Set([paymentMethodID]) : new Set()
                )
              }
            }}
            disallowEmptySelection
            className={styles.paymentMethodList}
          >
            {(paymentMethod) => {
              if ("paymentMethodID" in paymentMethod) {
                return (
                  <GridListItem
                    id={paymentMethod.paymentMethodID}
                    key={paymentMethod.paymentMethodID}
                    textValue={`${paymentMethod.cardBrand} •••• ${paymentMethod.last4}`}
                    className={styles.paymentMethod}
                  >
                    <div className="radio">
                      <div className="selectedIndicator" />
                    </div>
                    <div className={styles.brand}>
                      <Icon
                        name={apiCardBrandToIconName(paymentMethod)}
                        retainOriginalColors
                      />
                    </div>
                    <div>
                      <Text className="bold">
                        •••• {paymentMethod.last4}{" "}
                        {paymentMethod.isDefault && (
                          <span>
                            ({t("register.express.defaultPaymentMethod")})
                          </span>
                        )}
                      </Text>
                    </div>
                  </GridListItem>
                )
              } else {
                return (
                  <GridListItem
                    id={CUSTOM_PAYMENT_METHOD}
                    key={CUSTOM_PAYMENT_METHOD}
                    textValue={t("register.express.useDifferentPaymentMethod")}
                    className={styles.paymentMethod}
                  >
                    <div className="radio">
                      <div className="selectedIndicator" />
                    </div>
                    <Text className="bold">
                      {t("register.express.useDifferentPaymentMethod")}
                    </Text>
                  </GridListItem>
                )
              }
            }}
          </GridList>
        )}
        <Button
          hue="secondary"
          variant="default"
          isPending={isPending}
          onPress={() => mutate()}
        >
          {t("common.continue")}
        </Button>
        <PayingCashLauncher
          sessionID={sessionID}
          onSuccess={() =>
            navigate({
              to: "/qr/$qrLookupID/gated/register/$sessionID/complete",
            })
          }
        />
      </Card>
    </>
  )

  async function mutationFn() {
    const authSession = await fetchAuthSession()
    const cognitoIDToken = authSession.tokens?.idToken?.toString()
    const paymentMethodID = paymentMethod.values().next().value?.toString()

    if (paymentMethodID === CUSTOM_PAYMENT_METHOD) {
      const lookupSessionRsp = await api.lookupSessions.POST({
        plate: sessionDetails.session.cameraPlate,
        method: "plate",
        bypassSessionsLookup: true,
      })

      if ("failReason" in lookupSessionRsp) {
        throw new GatedLookupSessionsError(lookupSessionRsp.failReason)
      }

      const rsp = await api.registerSession.POST({
        sessionID,
        isReregistering: false,
        isInLane: false,
        phone: me?.cognitoUser.phone_number || null,
      })

      if ("failReason" in rsp) {
        throw new GatedRegisterSessionError(rsp.failReason)
      }

      return rsp
    }

    if (!cognitoIDToken) {
      throw new GatedAutoPaySelectCardError(
        GatedAutoPaySelectCardFailReason.Unauthorized
      )
    }

    if (!paymentMethodID) {
      throw new GatedAutoPaySelectCardError(
        GatedAutoPaySelectCardFailReason.PaymentMethodNotFound
      )
    }

    const rsp = await api.autoPaySelectCard.POST({
      cognitoIDToken,
      paymentMethodID,
      sessionID,
      qrLookupID,
    })

    if ("failReason" in rsp) {
      throw new GatedAutoPaySelectCardError(rsp.failReason)
    }

    return rsp
  }

  function onSuccess(
    rsp:
      | GatedRegisterSessionPOSTSuccessResponse
      | GatedAutoPaySelectCardPOSTSuccessResponse
  ) {
    if ("session" in rsp) {
      navigate({ to: "/qr/$qrLookupID/gated/register/$sessionID/checkout" })
    } else {
      navigate({
        to: "/qr/$qrLookupID/gated/register/$sessionID/confirmation",
        search: { key: rsp.confirmationKey },
      })
    }
  }

  function onError(error: Error) {
    setError({ error })
  }
}

function VehicleCell(props: { plate: string }) {
  const { data: me } = useSuspenseQuery(meQueryOptions())
  const vehicle = me?.inflowUser.savedPlates.find(
    (v) => v.plate === props.plate
  )

  return (
    <div className={styles.detectedVehicle}>
      <div className={styles.iconContainer}>
        <Icon name="car" />
      </div>
      {vehicle?.nickname ? (
        <div>
          <Text className="semi-bold">{vehicle.nickname}</Text>
          <Text level={3}>{vehicle.plate}</Text>
        </div>
      ) : (
        <Text className="semi-bold">{props.plate}</Text>
      )}
    </div>
  )
}

function PromoCodeSection(props: {
  plate: string
  hasSavedPaymentMethods: boolean
}) {
  const { t } = useTranslation()
  const { sessionID } = Route.useParams()
  const navigate = Route.useNavigate()
  const queryClient = useQueryClient()
  const { data: me, isPending: isMePending } = useQuery(meQueryOptions())

  const [showDiscountCodeField, toggleShowDiscountCodeField] = useToggle(false)
  const [discountCodeApplied, setDiscountCodeApplied] = useState("")
  const form = useForm<DiscountCodeSchema>({
    defaultValues: {
      promoCode: "",
    },
  })

  const { mutate, isPending } = useMutation({
    mutationFn,
    onSuccess,
    throwOnError: true,
  })

  return (
    <>
      <Text
        level={2}
        className="center"
        style={{
          marginTop: props.hasSavedPaymentMethods
            ? "var(--space-2)"
            : undefined,
        }}
      >
        {!discountCodeApplied && (
          <Button
            hue="primary"
            variant="solid"
            presentation="link"
            onPress={() => {
              if (showDiscountCodeField) {
                setDiscountCodeApplied("")
              }
              toggleShowDiscountCodeField()
            }}
          >
            {showDiscountCodeField
              ? t("register.cart.promoCode.remove")
              : t("register.cart.promoCode.add")}
          </Button>
        )}
      </Text>
      {
        <form onSubmit={form.handleSubmit(onSubmit)}>
          {showDiscountCodeField && !discountCodeApplied && (
            <fieldset disabled={isPending}>
              <Controller<DiscountCodeSchema>
                name="promoCode"
                control={form.control}
                rules={{ required: requiredRule(t("errors.common.required")) }}
                render={({
                  field: { ref, onChange, ...rest },
                  fieldState: { error, invalid },
                }) => (
                  <TextField
                    {...rest}
                    onChange={(v) => onChange(v.toUpperCase())}
                    isInvalid={invalid}
                    aria-label={t("register.cart.promoCode.fieldLabel")}
                  >
                    <div className="promoCodeField">
                      <TextField.Input innerRef={ref} style={{ width: 0 }} />
                      <Button
                        type="submit"
                        hue="neutral"
                        variant="default"
                        isPending={isPending}
                        isDisabled={isMePending}
                        style={{ width: "fit-content", flexShrink: 0 }}
                      >
                        {t("register.cart.promoCode.apply")}
                      </Button>
                    </div>
                    <FieldError>{error?.message}</FieldError>
                  </TextField>
                )}
              />
            </fieldset>
          )}
          {discountCodeApplied && (
            <Text level={2} className="highlighted-cell center">
              <b>{discountCodeApplied}</b>{" "}
              {t("register.cart.promoCode.successfullyApplied")}
            </Text>
          )}
        </form>
      }
    </>
  )

  function onSubmit(data: DiscountCodeSchema) {
    mutate(data)
  }

  async function mutationFn(request: GatedPromoRegisterPOSTRequest) {
    const lookupSessionRsp = await api.lookupSessions.POST({
      plate: props.plate,
      method: "plate",
      bypassSessionsLookup: true,
    })

    if ("failReason" in lookupSessionRsp) {
      throw new GatedLookupSessionsError(lookupSessionRsp.failReason)
    }

    const registerRsp = await api.registerSession.POST({
      sessionID,
      isReregistering: false,
      isInLane: false,
      phone: me?.cognitoUser.phone_number || null,
    })

    if ("failReason" in registerRsp) {
      throw new GatedRegisterSessionError(registerRsp.failReason)
    }

    const rsp = await api.promoRegister.POST(request)
    if ("failReason" in rsp) {
      const shouldThrow = [
        PromoFailReason.InvalidRequestFormat,
        PromoFailReason.SystemError,
        PromoFailReason.Unauthorized,
      ]
      const error = new GatedPromoRegisterError(rsp.failReason)
      if (shouldThrow.some((r) => r === rsp.failReason)) {
        throw error
      }

      form.setError("promoCode", { message: t(error.translationKey) })
      return
    }
    return rsp
  }

  function onSuccess(
    rsp: GatedPromoRegisterPOSTSuccessResponse | undefined,
    variables: GatedPromoRegisterPOSTRequest
  ) {
    if (!rsp) return
    if (rsp.bypassCC) {
      navigate({
        to: "/qr/$qrLookupID/gated/register/$sessionID/confirmation",
        search: { key: rsp.confirmationKey! },
      })
    } else {
      queryClient.removeQueries(setupIntentQueryOptions())
      setDiscountCodeApplied(variables.promoCode)
    }
  }
}
